import { Internal } from '@gleamer/types';
import { utils as gleamerUtils } from '@gleamer/ui';
import {
  BRUSH_TOOL_NAME,
  BrushToolGroup,
  clampBrushSize,
  getActiveBrushToolName,
  getNextBrushSize,
  getOtherBrushToolName,
  isBrushToolName,
} from './brush';

type Tool = Internal.Tool;

type ToolGroup = BrushToolGroup & {
  setToolActive: (toolName: string, options: { bindings: unknown[] }) => void;
  setToolPassive: (
    toolName: string,
    options?: { removeAllBindings?: boolean }
  ) => void;
};

// Cornerstone's `setToolPassive` only strips the primary (left) binding, and
// keeps a tool with any other binding Active. The Brush is on the right button
// (`Annotation` bindings): without this, the variant put down keeps drawing,
// and after a switch the Brush paints where the Eraser should erase.
const PUT_DOWN = { removeAllBindings: true };

export type BrushCommandsDeps = {
  /** The task tool from the `tools` store; null outside a task. */
  getTaskTool: () => Tool | null;
  setTaskTool: (tool: Tool) => void;
  getActiveViewportId: () => string | undefined;
  getToolGroupForViewport: (
    viewportId: string
  ) => ToolGroup | null | undefined | void;
  getToolGroupIds: () => string[];
  getBrushSize: (toolGroupId: string) => number | undefined;
  setBrushSize: (toolGroupId: string, size: number) => void;
  /** The OHIF mode in the URL, e.g. `bac` for `/ohif/bac/...`. */
  getModeName: () => string | null;
  ensureDefaultSegment: (viewportId: string) => Promise<boolean>;
  getAnnotationBindings: () => unknown[];
  runCommand: (commandName: string) => void;
  onBrushSizeChanged: () => void;
};

/**
 * Brush commands. Inside a task the Brush is the task tool, so they go through
 * the `tools` store and let `useViewportTool` activate it. In the bac mode
 * there is no task, so they drive the tool group directly.
 */
export function createBrushCommands(deps: BrushCommandsDeps) {
  function getTaskBrushTool(): Tool | null {
    const taskTool = deps.getTaskTool();
    return isBrushToolName(taskTool?.name) ? taskTool : null;
  }

  function getActiveToolGroup() {
    const viewportId = deps.getActiveViewportId();
    return viewportId ? deps.getToolGroupForViewport(viewportId) || null : null;
  }

  function getActiveBrushTool(): string | null {
    return (
      getTaskBrushTool()?.name ?? getActiveBrushToolName(getActiveToolGroup())
    );
  }

  // Preparing the Segment can take seconds on a DBT: the latest click wins over
  // one still waiting for it.
  let latestBacRequest = 0;

  async function setBacBrushTool(toolName: string) {
    // Outside a task, only the bac mode offers the Brush, whatever the data source.
    if (deps.getModeName() !== gleamerUtils.BAC_MODE_NAME) {
      return;
    }
    const request = ++latestBacRequest;
    const viewportId = deps.getActiveViewportId();
    const toolGroup = getActiveToolGroup();
    if (!viewportId || !toolGroup) {
      return;
    }

    // Pressing the active tool's button again puts the Brush down.
    if (getActiveBrushToolName(toolGroup) === toolName) {
      toolGroup.setToolPassive(toolName, PUT_DOWN);
      return;
    }

    const isReady = await deps.ensureDefaultSegment(viewportId);
    if (!isReady || request !== latestBacRequest) {
      return;
    }
    // Only the other variant can be active here, and it shares the binding.
    const activeToolName = getActiveBrushToolName(toolGroup);
    if (activeToolName) {
      toolGroup.setToolPassive(activeToolName, PUT_DOWN);
    }
    toolGroup.setToolActive(toolName, {
      bindings: deps.getAnnotationBindings(),
    });
  }

  function setBrushSize(size: number) {
    deps.getToolGroupIds().forEach(toolGroupId => {
      deps.setBrushSize(toolGroupId, clampBrushSize(size));
    });
    deps.onBrushSizeChanged();
  }

  function changeBrushSize(direction: 'increase' | 'decrease') {
    const [toolGroupId] = deps.getToolGroupIds();
    const size = toolGroupId ? deps.getBrushSize(toolGroupId) : undefined;
    setBrushSize(getNextBrushSize(size, direction));
  }

  // `+` and `-` resize the Brush while it is active, and keep zooming
  // otherwise.
  function resizeBrushOrZoom(
    direction: 'increase' | 'decrease',
    zoomCommandName: string
  ) {
    if (getActiveBrushTool()) {
      changeBrushSize(direction);
      return;
    }
    deps.runCommand(zoomCommandName);
  }

  function setGleamerBrushTool({ toolName }: { toolName: string }) {
    const taskTool = getTaskBrushTool();
    if (taskTool) {
      deps.setTaskTool({ ...taskTool, name: toolName });
      return;
    }
    return setBacBrushTool(toolName);
  }

  return {
    isBrushActive: () => getActiveBrushTool() !== null,
    actions: {
      setGleamerBrushTool,
      toggleGleamerBrushEraser: () => {
        const activeToolName = getActiveBrushTool();
        return setGleamerBrushTool({
          toolName: activeToolName
            ? getOtherBrushToolName(activeToolName)
            : BRUSH_TOOL_NAME,
        });
      },
      setGleamerBrushSize: ({ value }: { value: number | string }) => {
        setBrushSize(Number(value));
      },
      increaseBrushSizeOrZoomIn: () =>
        resizeBrushOrZoom('increase', 'scaleUpViewport'),
      decreaseBrushSizeOrZoomOut: () =>
        resizeBrushOrZoom('decrease', 'scaleDownViewport'),
    },
  };
}
